import { AfterViewInit, ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, Output, ViewChild } from "@angular/core";
import { AddressValidationAttribute, OrderDetailIndividualAddressView } from "../../..";
import { cloneDeep } from 'lodash';
import { InsightPassengerAddressModel } from "src/app/core/models/individual-model";
import { NgForm } from "@angular/forms";
import { Subject, Subscription } from "rxjs";
import { filter, takeUntil } from "rxjs/operators";
import { AddressTypeSelectionComponent } from "src/app/core/components/address-type-selection";
import { CountrySelectionComponent } from "src/app/core/components/country-selection/country-selection.component";
import { isEqual } from "lodash";
import { OrganisationAttributeModel } from "src/app/core/models/organisation-model";
import { AddressTypeReferenceService, CountryReferenceService } from "src/app/core/services/system-services";
import { AddressTypeReferenceModel, CountryReferenceModel } from "src/app/core/models/reference-model/reference-general-model";

@Component({
    selector: 'op-order-detail-address-form',
    templateUrl: './order-detail-address-form.component.html'
})
export class OrderDetailAddressFormComponent implements AfterViewInit, OnDestroy {
    @Input() individualId: string;
    @Input('data') set data(view: OrderDetailIndividualAddressView) {
        this.view = cloneDeep(view);
    }
    @Input() organisationAttributes: OrganisationAttributeModel[];

    @Output() apply = new EventEmitter<OrderDetailIndividualAddressView>();
    @Output() close = new EventEmitter<void>();
    @Output() undo = new EventEmitter();

    @ViewChild(NgForm) form: NgForm;
    @ViewChild(AddressTypeSelectionComponent) addressTypeSelectionComponent: AddressTypeSelectionComponent;
    @ViewChild(CountrySelectionComponent) countrySelectionComponent: CountrySelectionComponent;

    get addressTypeRequired(): boolean {
        return this.organisationAttributes
            ?.some(a => a.organisationAttributeTypeCode == AddressValidationAttribute.Type);
    }

    get addressLine1Required(): boolean {
        return this.organisationAttributes
            ?.some(a => a.organisationAttributeTypeCode == AddressValidationAttribute.AddressLine1);
    }

    get addressLine2Required(): boolean {
        return this.organisationAttributes
            ?.some(a => a.organisationAttributeTypeCode == AddressValidationAttribute.AddressLine2);
    }

    get stateRequired(): boolean {
        return this.organisationAttributes
            ?.some(a => a.organisationAttributeTypeCode == AddressValidationAttribute.State);
    }

    get provinceRequired(): boolean {
        return this.organisationAttributes
            ?.some(a => a.organisationAttributeTypeCode == AddressValidationAttribute.Province);
    }

    get postalRequired(): boolean {
        return this.organisationAttributes
            ?.some(a => a.organisationAttributeTypeCode == AddressValidationAttribute.Postal);
    }

    get cityRequired(): boolean {
        return this.organisationAttributes
            ?.some(a => a.organisationAttributeTypeCode == AddressValidationAttribute.City);
    }

    get countryRequired(): boolean {
        return this.organisationAttributes
            ?.some(a => a.organisationAttributeTypeCode == AddressValidationAttribute.Country);
    }

    view: OrderDetailIndividualAddressView;
    focused: boolean = false;
    processing: boolean = false;

    private destroy$ = new Subject();
    private subscriptions: Subscription[]

    constructor(private changeDetector: ChangeDetectorRef,
        private countryReferenceService: CountryReferenceService,
        private addressTypeReferenceService: AddressTypeReferenceService
    ) {
        this.subscriptions = [];
    }

    ngAfterViewInit(): void {
        this.checkIfErrorPresist();
        this.subscriptToCountryReference();
        this.subscriptToAddressTypeReference();
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(s => s.unsubscribe())
        this.destroy$.next();
        this.destroy$.complete();
    }

    onPresetSelected(item: InsightPassengerAddressModel) {
        if (!item) return;
        this.view = {
            addressLine1: item.addressLine1,
            addressLine2: item.addressLine2,
            addressTypeCode: item.addressTypeCode,
            addressTypeName: item.addressTypeName,
            city: item.city,
            countryCode: item.countryCode,
            countryName: item.countryName,
            orderAddressRoleCode: this.view.orderAddressRoleCode,
            postalCode: item.postalCode,
            province: item.province,
            state: item.state,
            orderId: null,
            addressId: item.addressId,
            primaryFlag: false,
            orderSalesStatusCode: null
        }
    }

    onUndoClick() {
        this.undo.emit();
    }

    onApplyClick() {
        this.processing = true;

        if (!this.form.valid) return;
        if (isEqual(this.view, this.getEmptyView())) return;

        this.view.addressTypeName = this.addressTypeSelectionComponent.getSelectedText();
        this.view.countryName = this.countrySelectionComponent.getSelectedText();
        this.apply.emit(this.view);
    }

    onClearClick() {
        this.resetInputFields();
    }

    onCloseClick() {
        this.close.emit();
    }

    private getEmptyView(): OrderDetailIndividualAddressView {
        return {
            addressLine1: null,
            addressLine2: null,
            addressTypeCode: null,
            addressTypeName: null,
            city: null,
            countryCode: null,
            countryName: null,
            orderAddressRoleCode: null,
            postalCode: null,
            province: null,
            state: null,
            orderId: null,
            addressId: null,
            primaryFlag: null,
            orderSalesStatusCode: null
        };
    }

    private resetInputFields() {
        this.view = this.getEmptyView();
        this.processing = false;
    }

    private checkIfErrorPresist() {
        this.form.valueChanges
            .pipe(
                filter(_ => this.processing == true),
                takeUntil(this.destroy$)
            )
            .subscribe(_ => setTimeout(_ => this.changeDetector.markForCheck()));
    }

    private someFieldsHasValue(view: OrderDetailIndividualAddressView): boolean {
        return Object.keys(view).filter(key => key != 'addressTypeCode').some(key => !!view[key]);
    }

    private subscriptToCountryReference() {
        this.subscriptions.push(
            this.countryReferenceService.countryReference
                .subscribe(countryReference => {
                    this.checkCountryReference(countryReference);
                })
        )
    }

    private checkCountryReference(reference: CountryReferenceModel[]) {
        if (!(reference?.length > 0)) return;
        if (!reference.find(item => item.countryCode == this.view?.countryCode)) {
            this.view.countryCode = null;
            this.view.countryName = null;
        }
    }

    private subscriptToAddressTypeReference() {
        this.subscriptions.push(
            this.addressTypeReferenceService.addressTypeReference
                .subscribe(addressTypeReference => {
                    this.checkAddressTypeReference(addressTypeReference);
                })
        )
    }

    private checkAddressTypeReference(reference: AddressTypeReferenceModel[]) {
        if (!(reference?.length > 0)) return;
        if (!reference.find(item => item.addressTypeCode == this.view?.addressTypeCode)) {
            this.view.countryCode = null;
            this.view.countryName = null;
        }
    }
}